﻿import React, { useEffect } from 'react';

import { AccessCodesContent } from 'pages/access_codes/components/AccessCodesContent/AccessCodesContent';
import { AccessCodesDetails } from 'pages/access_codes/components/AccessCodesDetails/AccessCodesDetails';
import { contentRestrictedByInactiveOrders, getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { TransactionData, useAccessCodes, useTransactionDetails } from 'pages/access_codes/context';
import { ContentDeliverable, License, Transaction, TransactionApi } from 'apis/TransactionApi';
import { AccessCodeTab } from 'pages/access_codes/components/AccessCodeTab/AccessCodeTab';
import { XedReportsTab } from 'pages/access_codes/components/XedReportsTab/XedReportsTab';
import { ActivityTab } from 'pages/access_codes/components/ActivityTab/ActivityTab';
import { StudentsTab } from 'pages/access_codes/components/StudentsTab/StudentsTab';
import { Certificates } from 'pages/students/components/Certificates/Certificates';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { XedTab } from 'pages/access_codes/components/XedTab/XedTab';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { useStickyTabIndex, useSuperState } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Tab } from 'components/Tabs/Tabs';
import { useJoyride } from 'app/joyride';
import { getFormattedDate } from 'utils';

export type ContentTiedToExpiredOrder = {
    content: ContentDeliverable[];
    expiredOrder: License;
};

interface TransactionProfileProps extends Tab<TransactionData> {
    contentTiedToInactiveOrder: boolean;
    handleDuplicateTransaction: (dataItem: Transaction, setDuplicateInProcess?: any) => Promise<void>;
    onDetailsItemClick: (dataItem: Transaction, newTransaction?: boolean) => Promise<void>;
    setInactiveOrdersWContentOnOpenTransaction: React.Dispatch<React.SetStateAction<ContentTiedToExpiredOrder[]>>;
}

export function TransactionProfile(props: TransactionProfileProps) {

    const { contentTiedToInactiveOrder, handleDuplicateTransaction, setInactiveOrdersWContentOnOpenTransaction } = props;

    const [state, setState] = useSuperState({
        selectedTabStripIndex: 0,
        subTile: undefined as ContentDeliverable | undefined
    });

    const { selectedTabStripIndex, subTile } = state;

    const { openTabIndex, pageContext, setSubTabIndex, subTabIndex, update, updateById } = useTransactionDetails();
    const { permissions } = useAccessCodes();
    const { catchWithToast } = useNotification();
    const { formatMessage } = useIntl();
    const { loadCurrentTour } = useJoyride();

    // Remember the active sub-tab per access code across refreshes.
    useStickyTabIndex({
        entityId: pageContext?.Id,
        onRestore: index => {

            const copyOfSubTabIndex = [...subTabIndex];
            copyOfSubTabIndex[openTabIndex - 1] = index;
            setSubTabIndex(copyOfSubTabIndex);
            setState({ selectedTabStripIndex: index });

        },
        scope: 'accessCode',
        value: subTabIndex[openTabIndex - 1] ?? 0
    });

    useEffect(() => {

        loadCurrentTour('ContentTab');

    }, []);

    useEffect(() => {

        if (
            pageContext &&
            pageContext.Data.inactiveOrders &&
            pageContext.Data.inactiveOrders.length > 0 &&
            pageContext.Data.content
        ) {

            const ordersAndContent = contentRestrictedByInactiveOrders(
                pageContext.Data.content,
                pageContext.Data.inactiveOrders
            );
            if (ordersAndContent.length > 0) {

                setInactiveOrdersWContentOnOpenTransaction(ordersAndContent);

            } else {

                setInactiveOrdersWContentOnOpenTransaction([]);

            }

        } else {

            setInactiveOrdersWContentOnOpenTransaction([]);

        }

    }, [pageContext?.Id, pageContext?.Data.inactiveOrders]);

    useEffect(() => {

        // setGlobalTransactionDetails();
        if (pageContext?.Data.licenses && pageContext.Data.content && !pageContext.Data.mostRestrictiveLicense) {

            update({
                mostRestrictiveLicense: getMostRestrictedLicenseDeliverable(
                    pageContext.Data.licenses,
                    pageContext.Data.content,
                    pageContext?.Data.transactionDetails?.HasXED === true
                )
            });

        }

    }, [pageContext?.Data.licenses, pageContext?.Data.content, pageContext?.Data.mostRestrictiveLicense]);

    // synchronize XED changes when moving away from tab index 4 (XED tab)
    const syncXEDChanges = async (newTabIdx: number) => {

        if (selectedTabStripIndex === 4 && newTabIdx !== 4) {

            if (
                pageContext &&
                pageContext.Data &&
                pageContext.Data.transactionDetails &&
                pageContext.Data.transactionDetails.HasXED === true &&
                pageContext.Data.transactionDetails.XEDExternalId &&
                pageContext.Data.transactionDetails.XEDExternalId.length > 0
            ) {

                await TransactionApi.SyncXEDGroup(pageContext.Data.transactionDetails.Id, -1, -1);

                let assignedContent = await TransactionApi.GetLimitedContentByTransactionId(
                    pageContext.Data.transactionDetails.Id,
                    pageContext.Data.transactionDetails.PartnerId
                ).catch(catchWithToast(formatMessage({ id: 'failed_to_load_assigned_content.3392953e', defaultMessage: 'Failed to load assigned content.' })));

                if (assignedContent && assignedContent.length > 0) {

                    assignedContent = assignedContent.map(content => {

                        if (content.ExpirationDate && content.StartDate && content.ExpirationDateString) {

                            content.ExpirationDate = new Date(content.ExpirationDate);
                            content.ExpirationDateString = getFormattedDate(new Date(content.ExpirationDateString));
                            content.StartDate = new Date(content.StartDate);
                            content.StartDateString = getFormattedDate(new Date(content.StartDateString));

                        }
                        return content;

                    });

                    updateById(pageContext.Data.transactionDetails.Id, {
                        content: assignedContent,
                        contentOriginal: assignedContent
                    });

                }

            }

        }

    };

    const tabNavigationClick = (value: number) => {

        const copyOfSubTabIndex = [...subTabIndex];
        copyOfSubTabIndex[openTabIndex - 1] = value;
        setSubTabIndex(copyOfSubTabIndex);
        syncXEDChanges(value);
        setState({ selectedTabStripIndex: value });

    };

    return (
        <>
            <GMetrixTabs
                unmountOnExit
                onSelect={i => tabNavigationClick(i)}
                selected={subTabIndex[openTabIndex - 1]}
            >
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-content-tab">
                            <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                        </div>
                    )}
                >
                    <AccessCodesDetails
                        contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                        handleDuplicateTransaction={handleDuplicateTransaction}
                        tabNavigationClick={tabNavigationClick}
                    />

                </GMetrixTabsTab>
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-content-tab">
                            <FormattedMessage defaultMessage="Content" id="content.e305279d" />
                        </div>
                    )}
                >
                    {pageContext?.Data.secondaryLoading ? (
                        <GMetrixLoader />
                    ) : (
                        <AccessCodesContent
                            contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                            permissions={permissions}

                        />
                    )}
                </GMetrixTabsTab>
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-students-tab">
                            <FormattedMessage defaultMessage="Students" description="menu" id="students.c6d199ef" />
                        </div>
                    )}
                >
                    {pageContext?.Data.secondaryLoading ? (
                        <GMetrixLoader />
                    ) : (
                        <StudentsTab
                            contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                            mode="Edit"
                        />
                    )}
                </GMetrixTabsTab>

                <GMetrixTabsTab
                    title={(
                        <div className="joyride-accessCodes-tab">
                            <FormattedMessage defaultMessage="Codes" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="codes.6ff8cf24" />
                        </div>
                    )}
                >
                    <AccessCodeTab
                        contentTiedToInactiveOrder={contentTiedToInactiveOrder}
                        handleDuplicateTransaction={handleDuplicateTransaction}
                    />
                </GMetrixTabsTab>

                <GMetrixTabsTab
                    title={(
                        <div className="joyride-activity-tab">
                            <FormattedMessage defaultMessage="Reports" id="reports.c01b78b6" />
                        </div>
                    )}
                >
                    <ActivityTab />
                </GMetrixTabsTab>

                {pageContext?.Data.transactionDetails?.HasXED === true && (
                    <GMetrixTabsTab
                        title={(
                            <div className="joyride-xed-tab">
                                <FormattedMessage defaultMessage="XED Settings" id="xed_settings.90e94a97" />
                            </div>
                        )}
                    >
                        <XedTab />
                    </GMetrixTabsTab>
                )}

                {pageContext?.Data.transactionDetails?.HasXED === true && (
                    <GMetrixTabsTab
                        title={(
                            <div className="joyride-xed-reports-tab">
                                <FormattedMessage defaultMessage="XED Reports" id="xed_reports.5c4e19c2" />
                            </div>
                        )}
                    >
                        <XedReportsTab />
                    </GMetrixTabsTab>
                )}
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-certificates-tab">

                            <FormattedMessage defaultMessage="Certificates" id="certificates.03accec8" />
                        </div>
                    )}
                >
                    <Certificates context="access-code" id={pageContext?.Data.transactionDetails?.Id} />
                </GMetrixTabsTab>
            </GMetrixTabs>
        </>
    );

}
